import { useState, useEffect, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Bar,
} from "recharts";
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  UserPlus, 
  UserMinus, 
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Info,
  Filter,
  Download,
  FileText,
  Image as ImageIcon,
  Loader2,
  ArrowRightLeft,
  AlertTriangle,
  AlertCircle,
  Sparkles,
  RefreshCw,
  Brain,
  CheckCircle,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import AdvancedFilters from "../components/AdvancedFilters";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Couleurs cohérentes pour les graphiques
const COLORS = {
  primary: "#1B4D3E",
  secondary: "#D4AF37",
  success: "#10B981",
  danger: "#EF4444",
  warning: "#F59E0B",
  info: "#0EA5E9",
  purple: "#8B5CF6",
  pink: "#EC4899",
};

// Formateur de nombres
const formatNumber = (num) => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}Md`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num?.toLocaleString("fr-FR") || "0";
};

// Formateur monétaire GNF
const formatCurrency = (num) => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(2)} Md GNF`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)} M GNF`;
  return `${num?.toLocaleString("fr-FR")} GNF`;
};

// Composant pour afficher la variation
const TrendIndicator = ({ current, previous, suffix = "" }) => {
  if (!previous || previous === 0) return <span className="text-gray-400">-</span>;
  
  const change = ((current - previous) / previous) * 100;
  const isPositive = change > 0;
  const isNeutral = Math.abs(change) < 0.5;
  
  if (isNeutral) {
    return (
      <span className="flex items-center text-gray-500 text-sm">
        <Minus className="w-4 h-4 mr-1" />
        Stable
      </span>
    );
  }
  
  return (
    <span className={`flex items-center text-sm ${isPositive ? "text-green-600" : "text-red-600"}`}>
      {isPositive ? (
        <ArrowUpRight className="w-4 h-4 mr-1" />
      ) : (
        <ArrowDownRight className="w-4 h-4 mr-1" />
      )}
      {Math.abs(change).toFixed(1)}%{suffix}
    </span>
  );
};

// Tooltip personnalisé
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border">
        <p className="font-semibold text-gray-900 mb-2">Année {label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatter ? formatter(entry.value) : formatNumber(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [regionTrends, setRegionTrends] = useState({});
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [yearsAvailable, setYearsAvailable] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [exporting, setExporting] = useState(false);
  
  // États pour Analyses Avancées
  const [analysisTab, setAnalysisTab] = useState("comparison");
  const [selectedYear1, setSelectedYear1] = useState("");
  const [selectedYear2, setSelectedYear2] = useState("");
  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [anomalies, setAnomalies] = useState([]);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  
  // Refs for export
  const chartsContainerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Initialiser les années pour la comparaison
  useEffect(() => {
    if (yearsAvailable.length >= 2) {
      setSelectedYear1(String(yearsAvailable[yearsAvailable.length - 2]));
      setSelectedYear2(String(yearsAvailable[yearsAvailable.length - 1]));
    }
  }, [yearsAvailable]);

  const fetchData = async () => {
    try {
      const [trendsRes, regionRes] = await Promise.all([
        axios.get(`${API_URL}/statistics/trends`),
        axios.get(`${API_URL}/statistics/trends/by-region`),
      ]);
      
      setTrends(trendsRes.data.trends || []);
      setYearsAvailable(trendsRes.data.years_available || []);
      setRegionTrends(regionRes.data || {});
    } catch (error) {
      console.error(/* PROD */ "Erreur lors du chargement des données:", error);
      toast.error("Impossible de charger les données d'analytics");
    } finally {
      setLoading(false);
    }
  };

  // Fonctions pour Analyses Avancées
  const fetchComparison = async () => {
    if (!selectedYear1 || !selectedYear2) {
      toast.error("Veuillez sélectionner deux années");
      return;
    }
    setComparisonLoading(true);
    try {
      const response = await axios.get(`${API_URL}/statistics/compare/years`, {
        params: { year1: selectedYear1, year2: selectedYear2 }
      });
      setComparison(response.data);
    } catch (error) {
      toast.error("Erreur lors de la comparaison");
    } finally {
      setComparisonLoading(false);
    }
  };

  const fetchAnomalies = async () => {
    setAnomaliesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/statistics/anomalies`);
      setAnomalies(response.data.anomalies || []);
    } catch (error) {
      toast.error("Erreur lors de la détection des anomalies");
    } finally {
      setAnomaliesLoading(false);
    }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API_URL}/ai/analyze/batch`, {
        prompt: aiPrompt || "Analysez les tendances et détectez les anomalies dans les bilans sociaux."
      });
      setAiAnalysis(response.data);
      toast.success("Analyse IA terminée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse IA");
    } finally {
      setAiLoading(false);
    }
  };

  const getVariationBadge = (val1, val2) => {
    if (!val1 || !val2) return <Badge variant="secondary">-</Badge>;
    const variation = ((val2 - val1) / val1) * 100;
    if (variation > 5) return <Badge className="bg-green-100 text-green-800">+{variation.toFixed(1)}%</Badge>;
    if (variation < -5) return <Badge className="bg-red-100 text-red-800">{variation.toFixed(1)}%</Badge>;
    return <Badge variant="secondary">{variation > 0 ? "+" : ""}{variation.toFixed(1)}%</Badge>;
  };

  const getSeverityBadge = (severity) => {
    const config = {
      critical: { class: "bg-red-100 text-red-800", icon: AlertCircle },
      warning: { class: "bg-amber-100 text-amber-800", icon: AlertTriangle },
      info: { class: "bg-blue-100 text-blue-800", icon: Info }
    };
    const { class: className, icon: Icon } = config[severity] || config.info;
    return (
      <Badge className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {severity === "critical" ? "Critique" : severity === "warning" ? "Attention" : "Info"}
      </Badge>
    );
  };

  // Export functions
  const exportToPDF = async () => {
    if (!chartsContainerRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(chartsContainerRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add header
      pdf.setFillColor(27, 77, 62); // #1B4D3E
      pdf.rect(0, 0, 297, 25, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.text("INSPECTION GÉNÉRALE DU TRAVAIL - ANALYTICS", 148.5, 12, { align: 'center' });
      pdf.setFontSize(10);
      pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 148.5, 20, { align: 'center' });
      
      // Add chart image
      const imgWidth = 277;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 10, 30, imgWidth, Math.min(imgHeight, 165));
      
      // Add footer
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(8);
      pdf.text("www.igt.gov.gn | Tél: 613 13 93 93 | info@igt.gov.gn", 148.5, 200, { align: 'center' });
      
      pdf.save(`Analytics_IGT_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("Rapport PDF exporté avec succès");
    } catch (error) {
      console.error(/* PROD */ "Erreur export PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const exportToImage = async () => {
    if (!chartsContainerRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(chartsContainerRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const link = document.createElement('a');
      link.download = `Analytics_IGT_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success("Image exportée avec succès");
    } catch (error) {
      console.error(/* PROD */ "Erreur export image:", error);
      toast.error("Erreur lors de l'export image");
    } finally {
      setExporting(false);
    }
  };

  // Gestionnaire des filtres avancés
  const handleFiltersChange = (filters) => {
    setAdvancedFilters(filters);
    if (filters.region && filters.region !== "all") {
      setSelectedRegion(filters.region);
    }
  };

  // Calculer les KPIs globaux
  const getKPIs = () => {
    if (trends.length === 0) return null;
    
    const latest = trends[trends.length - 1];
    const previous = trends.length > 1 ? trends[trends.length - 2] : null;
    
    return {
      totalEffectif: latest?.total_effectif || 0,
      previousEffectif: previous?.total_effectif || 0,
      pctGuineens: latest?.pct_guineens || 0,
      pctFemmes: latest?.pct_femmes || 0,
      embauches: latest?.embauches || 0,
      previousEmbauches: previous?.embauches || 0,
      masseSalariale: latest?.masse_salariale || 0,
      previousMasseSalariale: previous?.masse_salariale || 0,
      soldeEmploi: latest?.solde_emploi || 0,
      latestYear: latest?.annee,
    };
  };

  const kpis = getKPIs();

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="analytics-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const noTrendsData = trends.length === 0;

  return (
    <div className="space-y-6 p-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-[#1B4D3E]" />
            Analytics & Tendances
          </h1>
          <p className="text-gray-500 mt-1">
            Évolution des indicateurs du bilan social au fil des années
          </p>
        </div>
        
        {/* Export Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="gap-2"
              disabled={exporting}
              data-testid="export-analytics-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Exporter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer">
              <FileText className="w-4 h-4 text-red-600" />
              Exporter en PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportToImage} className="gap-2 cursor-pointer">
              <ImageIcon className="w-4 h-4 text-blue-600" />
              Exporter en Image (PNG)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filtres avancés */}
      <Card>
        <CardContent className="p-4">
          <AdvancedFilters
            onFiltersChange={handleFiltersChange}
            showSearch={false}
            showStatut={false}
            showCabinet={true}
            showRegion={true}
            showSecteur={true}
            showPeriode={true}
            compact={true}
          />
        </CardContent>
      </Card>

      {/* Message si pas de données */}
      {noTrendsData && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Activity className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Pas encore de données de tendances
            </h2>
            <p className="text-gray-500">
              Les graphiques d'évolution apparaîtront une fois que des bilans sociaux seront validés.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Charts Container - for export */}
      {!noTrendsData && (
      <div ref={chartsContainerRef}>
      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Effectif Total ({kpis.latestYear})</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(kpis.totalEffectif)}
                  </p>
                  <TrendIndicator current={kpis.totalEffectif} previous={kpis.previousEffectif} />
                </div>
                <div className="p-3 bg-[#1B4D3E]/10 rounded-full">
                  <Users className="w-6 h-6 text-[#1B4D3E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Taux de Guinéisation</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {kpis.pctGuineens}%
                  </p>
                  <p className="text-sm text-gray-400">Femmes: {kpis.pctFemmes}%</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <UserPlus className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Embauches ({kpis.latestYear})</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(kpis.embauches)}
                  </p>
                  <TrendIndicator current={kpis.embauches} previous={kpis.previousEmbauches} />
                </div>
                <div className={`p-3 rounded-full ${kpis.soldeEmploi >= 0 ? "bg-green-100" : "bg-red-100"}`}>
                  {kpis.soldeEmploi >= 0 ? (
                    <UserPlus className="w-6 h-6 text-green-600" />
                  ) : (
                    <UserMinus className="w-6 h-6 text-red-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Masse Salariale</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(kpis.masseSalariale)}
                  </p>
                  <TrendIndicator current={kpis.masseSalariale} previous={kpis.previousMasseSalariale} />
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for different charts */}
      <Tabs defaultValue="effectifs" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 gap-2">
          <TabsTrigger value="effectifs">Effectifs</TabsTrigger>
          <TabsTrigger value="mouvements">Mouvements</TabsTrigger>
          <TabsTrigger value="remuneration">Rémunération</TabsTrigger>
          <TabsTrigger value="contrats">Contrats</TabsTrigger>
          <TabsTrigger value="demographics">Démographie</TabsTrigger>
        </TabsList>

        {/* Effectifs Tab */}
        <TabsContent value="effectifs">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Evolution des effectifs totaux */}
            <Card>
              <CardHeader>
                <CardTitle>Évolution des Effectifs</CardTitle>
                <CardDescription>
                  Nombre total d'employés déclarés par année
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends}>
                      <defs>
                        <linearGradient id="colorEffectif" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="total_effectif" 
                        name="Effectif Total"
                        stroke={COLORS.primary} 
                        fill="url(#colorEffectif)"
                        strokeWidth={3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Répartition Guinéens vs Étrangers */}
            <Card>
              <CardHeader>
                <CardTitle>Guinéens vs Étrangers</CardTitle>
                <CardDescription>
                  Évolution de la répartition par nationalité
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="total_guineens" 
                        name="Guinéens"
                        stroke={COLORS.primary} 
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="total_etrangers" 
                        name="Étrangers"
                        stroke={COLORS.secondary} 
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Répartition Hommes vs Femmes */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Répartition par Genre</CardTitle>
                <CardDescription>
                  Évolution de la parité hommes/femmes dans les entreprises
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends}>
                      <defs>
                        <linearGradient id="colorHommes" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.info} stopOpacity={0.6}/>
                          <stop offset="95%" stopColor={COLORS.info} stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="colorFemmes" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.pink} stopOpacity={0.6}/>
                          <stop offset="95%" stopColor={COLORS.pink} stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="total_hommes" 
                        name="Hommes"
                        stroke={COLORS.info} 
                        fill="url(#colorHommes)"
                        strokeWidth={2}
                        stackId="1"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="total_femmes" 
                        name="Femmes"
                        stroke={COLORS.pink} 
                        fill="url(#colorFemmes)"
                        strokeWidth={2}
                        stackId="1"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Mouvements Tab */}
        <TabsContent value="mouvements">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Embauches vs Départs */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Embauches vs Départs</CardTitle>
                <CardDescription>
                  Comparaison annuelle des flux d'entrées et sorties
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar 
                        dataKey="embauches" 
                        name="Embauches" 
                        fill={COLORS.success} 
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="total_departs" 
                        name="Départs" 
                        fill={COLORS.danger} 
                        radius={[4, 4, 0, 0]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="solde_emploi" 
                        name="Solde Net"
                        stroke={COLORS.primary} 
                        strokeWidth={3}
                        dot={{ r: 5, fill: COLORS.primary }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Détail des départs */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Détail des Motifs de Départ</CardTitle>
                <CardDescription>
                  Répartition des départs par type
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="licenciements" name="Licenciements" stroke={COLORS.danger} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="demissions" name="Démissions" stroke={COLORS.warning} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="retraites" name="Retraites" stroke={COLORS.info} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="fin_contrat" name="Fin de Contrat" stroke={COLORS.purple} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Rémunération Tab */}
        <TabsContent value="remuneration">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Évolution de la Masse Salariale</CardTitle>
                <CardDescription>
                  Total des rémunérations versées par année (en GNF)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends}>
                      <defs>
                        <linearGradient id="colorSalaire" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.4}/>
                          <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={(v) => `${(v / 1000000000).toFixed(0)}Md`} />
                      <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                      <Area 
                        type="monotone" 
                        dataKey="masse_salariale" 
                        name="Masse Salariale"
                        stroke={COLORS.secondary} 
                        fill="url(#colorSalaire)"
                        strokeWidth={3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Cotisations CNSS</CardTitle>
                <CardDescription>
                  Total des cotisations sociales versées par année
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
                      <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                      <Line 
                        type="monotone" 
                        dataKey="total_cnss" 
                        name="Cotisations CNSS"
                        stroke={COLORS.info} 
                        strokeWidth={3}
                        dot={{ r: 5, fill: COLORS.info }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contrats Tab */}
        <TabsContent value="contrats">
          <Card>
            <CardHeader>
              <CardTitle>Évolution des Types de Contrats</CardTitle>
              <CardDescription>
                Répartition CDI vs CDD au fil des années
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trends}>
                    <defs>
                      <linearGradient id="colorCDI" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.6}/>
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorCDD" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.warning} stopOpacity={0.6}/>
                        <stop offset="95%" stopColor={COLORS.warning} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="annee" />
                    <YAxis tickFormatter={formatNumber} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="cdi" 
                      name="CDI"
                      stroke={COLORS.primary} 
                      fill="url(#colorCDI)"
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="cdd" 
                      name="CDD"
                      stroke={COLORS.warning} 
                      fill="url(#colorCDD)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Demographics Tab */}
        <TabsContent value="demographics">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Catégories Professionnelles</CardTitle>
                <CardDescription>
                  Évolution par catégorie socio-professionnelle
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="cadres" name="Cadres" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="agents_maitrise" name="Agents de Maîtrise" stroke={COLORS.secondary} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="employes" name="Employés" stroke={COLORS.info} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="ouvriers" name="Ouvriers" stroke={COLORS.purple} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pyramide des Âges</CardTitle>
                <CardDescription>
                  Évolution de la répartition par tranches d'âge
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="annee" />
                      <YAxis tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="age_16_21" name="16-21 ans" stroke="#10B981" strokeWidth={2} />
                      <Line type="monotone" dataKey="age_22_30" name="22-30 ans" stroke="#0EA5E9" strokeWidth={2} />
                      <Line type="monotone" dataKey="age_31_40" name="31-40 ans" stroke="#8B5CF6" strokeWidth={2} />
                      <Line type="monotone" dataKey="age_41_50" name="41-50 ans" stroke="#F59E0B" strokeWidth={2} />
                      <Line type="monotone" dataKey="age_51_60" name="51-60 ans" stroke="#EC4899" strokeWidth={2} />
                      <Line type="monotone" dataKey="age_61_65" name="61+ ans" stroke="#EF4444" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      </div>
      )}
      {/* End of charts container */}

      {/* Info panel - show only when there's data */}
      {!noTrendsData && (
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">À propos des données</p>
              <p className="mt-1 text-blue-700">
                Ces graphiques sont basés sur les <strong>{trends.reduce((acc, t) => acc + t.bilans_count, 0)} bilans sociaux validés</strong> couvrant 
                les années <strong>{yearsAvailable.length > 0 ? `${yearsAvailable[0]} à ${yearsAvailable[yearsAvailable.length - 1]}` : "N/A"}</strong>.
                Les tendances reflètent l'évolution des données agrégées de toutes les entreprises déclarantes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Section Analyses Avancées */}
      <Separator className="my-8" />
      
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Brain className="w-7 h-7 text-purple-600" />
          <div>
            <h2 className="text-xl font-bold text-slate-900">Analyses Avancées</h2>
            <p className="text-muted-foreground text-sm">Comparaisons, détection d'anomalies et analyse IA</p>
          </div>
        </div>

        <Tabs value={analysisTab} onValueChange={setAnalysisTab}>
          <TabsList className="grid w-full max-w-xl grid-cols-3">
            <TabsTrigger value="comparison" className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" />
              Comparaison
            </TabsTrigger>
            <TabsTrigger value="anomalies" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Anomalies
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Analyse IA
            </TabsTrigger>
          </TabsList>

          {/* Tab Comparaison */}
          <TabsContent value="comparison" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                  Comparaison Inter-Annuelle
                </CardTitle>
                <CardDescription>
                  Comparez les indicateurs clés entre deux années
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Année de référence</label>
                    <Select value={selectedYear1} onValueChange={setSelectedYear1}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearsAvailable.map(year => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Année à comparer</label>
                    <Select value={selectedYear2} onValueChange={setSelectedYear2}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearsAvailable.map(year => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchComparison} disabled={comparisonLoading}>
                    {comparisonLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Comparer
                  </Button>
                </div>

                {comparison && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Indicateur</TableHead>
                        <TableHead className="text-right">{selectedYear1}</TableHead>
                        <TableHead className="text-right">{selectedYear2}</TableHead>
                        <TableHead className="text-right">Variation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { key: "effectif_total", label: "Effectif Total" },
                        { key: "masse_salariale", label: "Masse Salariale", isCurrency: true },
                        { key: "embauches", label: "Embauches" },
                        { key: "departs", label: "Départs" },
                        { key: "accidents", label: "Accidents de travail" },
                        { key: "formations", label: "Salariés formés" },
                      ].map(item => (
                        <TableRow key={item.key}>
                          <TableCell className="font-medium">{item.label}</TableCell>
                          <TableCell className="text-right">
                            {item.isCurrency 
                              ? formatCurrency(comparison.year1?.[item.key] || 0)
                              : formatNumber(comparison.year1?.[item.key] || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.isCurrency 
                              ? formatCurrency(comparison.year2?.[item.key] || 0)
                              : formatNumber(comparison.year2?.[item.key] || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {getVariationBadge(comparison.year1?.[item.key], comparison.year2?.[item.key])}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {!comparison && !comparisonLoading && (
                  <div className="text-center py-8 text-muted-foreground">
                    <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Sélectionnez deux années et cliquez sur "Comparer"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Anomalies */}
          <TabsContent value="anomalies" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Détection d'Anomalies
                  </CardTitle>
                  <CardDescription>
                    Identifiez les valeurs inhabituelles dans les bilans sociaux
                  </CardDescription>
                </div>
                <Button onClick={fetchAnomalies} disabled={anomaliesLoading}>
                  {anomaliesLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Analyser
                </Button>
              </CardHeader>
              <CardContent>
                {anomaliesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                  </div>
                ) : anomalies.length > 0 ? (
                  <div className="space-y-4">
                    {anomalies.map((anomaly, index) => (
                      <div key={index} className="p-4 rounded-lg border bg-gray-50">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getSeverityBadge(anomaly.severity)}
                              <span className="font-medium">{anomaly.enterprise_name}</span>
                              <Badge variant="outline">{anomaly.annee}</Badge>
                            </div>
                            <p className="text-sm text-gray-600">{anomaly.description}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Indicateur: {anomaly.indicator} | Valeur: {anomaly.value} | Seuil: {anomaly.threshold}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>Aucune anomalie détectée</p>
                    <p className="text-sm mt-1">Cliquez sur "Analyser" pour lancer la détection</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Analyse IA */}
          <TabsContent value="ai" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Analyse par Intelligence Artificielle
                </CardTitle>
                <CardDescription>
                  Obtenez des insights et recommandations basés sur vos données
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Question ou contexte (optionnel)</label>
                  <Textarea
                    placeholder="Ex: Quelles sont les tendances principales dans les effectifs ? Y a-t-il des secteurs à risque ?"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    rows={3}
                  />
                </div>
                
                <Button 
                  onClick={runAiAnalysis} 
                  disabled={aiLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {aiLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  Lancer l'analyse IA
                </Button>

                {aiAnalysis && (
                  <div className="mt-6 space-y-4">
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-purple-900 mb-2">Résultat de l'analyse</p>
                          <div className="prose prose-sm max-w-none text-purple-800 whitespace-pre-wrap">
                            {aiAnalysis.analysis || aiAnalysis.result || JSON.stringify(aiAnalysis, null, 2)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {aiAnalysis.recommendations && (
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="font-medium text-emerald-900 mb-2">Recommandations</p>
                        <ul className="list-disc list-inside text-sm text-emerald-800 space-y-1">
                          {(Array.isArray(aiAnalysis.recommendations) 
                            ? aiAnalysis.recommendations 
                            : [aiAnalysis.recommendations]
                          ).map((rec, i) => (
                            <li key={i}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {!aiAnalysis && !aiLoading && (
                  <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>L'IA analysera vos données et générera des insights</p>
                    <p className="text-sm mt-1">Cliquez sur "Lancer l'analyse IA" pour commencer</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
